import { useQuery } from '@apollo/client';
import { useState as useHookState } from '@hookstate/core';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { adminState } from '../../../adminState';
import {
  GetVrBoxesDocument,
  GetVrBoxesQuery,
  GetVrDevicesDocument,
  GetVrDevicesQuery,
  UserPermission,
} from '../../../graphql/graphql-operations';
import { compareByProp } from '../../../lib/compare';
import { BOX_ID_KEY } from '../../../lib/localStorageKeys';
import { useUser } from '../UserProvider/useUserHook';
import { handleDeviceEventUpdate } from './deviceEventHandler';
import { useBatchedVrDevicesSubscription } from './useBatchedVrDevicesSubscription';

interface Props {}

export type VrDeviceType = GetVrDevicesQuery['vrDevices'][0];

export interface VrDevicesContextType {
  loading: boolean;
  devices: GetVrDevicesQuery['vrDevices'];
  boxDevices: GetVrDevicesQuery['vrDevices'];
  hasActiveSession: boolean;
  vrBoxes: GetVrBoxesQuery['vrBoxes'];
  vrBoxId: string | null;
  setVrBoxId: (vrBoxId: string | null) => void;
}

export const VrDevicesContext = createContext<VrDevicesContextType>({
  loading: true,
  devices: [],
  boxDevices: [],
  hasActiveSession: false,
  vrBoxId: null,
  vrBoxes: [],
  setVrBoxId: () => {},
});

export const setVrBoxIdToLocalStorage = (vrBoxId: string | null) => {
  if (vrBoxId) {
    localStorage.setItem(BOX_ID_KEY, vrBoxId);
  } else {
    localStorage.removeItem(BOX_ID_KEY);
  }
};

export const getVrBoxIdFromLocalStorage = () => {
  return localStorage.getItem(BOX_ID_KEY);
};

export const useBoxDevices = () => {
  return useContextSelector(VrDevicesContext, (v) => v.boxDevices);
};

export const useDevicesLoading = () => {
  return useContextSelector(VrDevicesContext, (v) => v.loading);
};
export const useDevices = () => {
  return useContextSelector(VrDevicesContext, (v) => v.devices);
};

export const useSetVrBoxId = () => {
  return useContextSelector(VrDevicesContext, (v) => v.setVrBoxId);
};
export const useVrBoxId = () => {
  return useContextSelector(VrDevicesContext, (v) => v.vrBoxId);
};

export const useVrBoxes = () => {
  return useContextSelector(VrDevicesContext, (v) => v.vrBoxes);
};

const VrDevicesProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
}: PropsWithChildren<Props>) => {
  const selectedCompanyId = useHookState(adminState.selectedCompanyId).get();
  // memoize compnayId to prevent unnecessary resubscriptions
  const companyId = useMemo(() => selectedCompanyId, [selectedCompanyId]);
  const [vrBoxId, setVrBoxId] = useState<string | null>(null);
  const { hasPermission } = useUser();
  const { data: vrBoxData } = useQuery(GetVrBoxesDocument, {
    variables: {
      companyIdOptionalInput: {
        companyId,
      },
    },
    skip: !hasPermission(UserPermission.READ_COMPANY_VR_BOXES),
  });
  const {
    data: devicesData,
    loading,
    subscribeToMore,
    updateQuery,
  } = useQuery(GetVrDevicesDocument, {
    variables: {
      companyIdOptionalInput: {
        companyId,
      },
    },
  });
  updateQuery((prev) => {
    return prev;
  });
  const vrDevices = useMemo(() => {
    return [...(devicesData?.vrDevices || [])].sort(
      compareByProp('name', 'asc'),
    );
  }, [devicesData]);

  useEffect(() => {
    const boxCode = getVrBoxIdFromLocalStorage();
    if (!vrBoxData?.vrBoxes) {
      return;
    }
    const vrBox = vrBoxData.vrBoxes.find((box) => box.id === boxCode);
    if (vrBox) {
      setVrBoxId(vrBox.id);
    }
  }, [vrBoxData?.vrBoxes]);

  useBatchedVrDevicesSubscription(
    subscribeToMore,
    companyId,
    (events) => {
      updateQuery((prev) => {
        let devices = prev.vrDevices || [];
        for (const updateEvent of events) {
          devices = handleDeviceEventUpdate(devices, updateEvent);
        }
        return { ...prev, vrDevices: devices };
      });
    },
    1000,
  );

  const hasActiveSession =
    vrDevices.filter((device) => device.session?.id).length > 0;

  const handleSetVrBoxId = (vrBoxId: string | null) => {
    setVrBoxIdToLocalStorage(vrBoxId);
    setVrBoxId(vrBoxId || null);
  };

  const boxDevices = useMemo(() => {
    return vrDevices.filter((dev) => !vrBoxId || dev.vrBoxId === vrBoxId);
  }, [vrBoxId, vrDevices]);
  return (
    <VrDevicesContext.Provider
      value={{
        loading,
        devices: vrDevices,
        boxDevices: boxDevices,
        hasActiveSession,
        vrBoxes: vrBoxData?.vrBoxes || [],
        vrBoxId,
        setVrBoxId: handleSetVrBoxId,
      }}
    >
      {children}
    </VrDevicesContext.Provider>
  );
};

export default VrDevicesProvider;
